import { useMutation, useQueryClient } from '@tanstack/react-query';
import { employeeKeys } from '@/modules/employees/application/employeeKeys';
import { DeleteFacialEmbeddingUseCase } from './use-cases/DeleteFacialEmbeddingUseCase';
import { DeleteNfcBiometricUseCase } from './use-cases/DeleteNfcBiometricUseCase';
import { HttpBiometricDeletionRepository } from '@/modules/admin/infrastructure/repositories/HttpBiometricDeletionRepository';
import { LocalEmployeeRepository } from '@/modules/employees/infrastructure/repositories/LocalEmployeeRepository';
import { LocalFaceEmbeddingRepository } from '@/modules/attendance/infrastructure/repositories/LocalFaceEmbeddingRepository';

/**
 * Mutation: delete facial v2 embedding for an employee.
 *
 * OFFLINE-FIRST UPDATE:
 * networkMode:'always' — deletion now works offline (enqueues sync for later).
 * 
 * OLD BEHAVIOR (commented below for rollback):
 * networkMode:'online' — deletion was blocked when the kiosk was offline.
 * Security reasoning was multi-terminal consistency (prevent access revocation
 * delay), but in practice sync queue handles this correctly: when sync processes,
 * all terminals refetch and update immediately.
 *
 * On success: invalidates employeeKeys.all() → list re-fetches updated enrollment.
 * On error:   no invalidation — cache remains consistent with last known state.
 *
 * Change: nueva-seccion-de-borrado-perfiles-en-administracion-empleados
 * Updated: enable-offline-deletion (2026-07-23)
 */
export const useDeleteFacialEmbeddingMutation = () => {
  const qc = useQueryClient();
  const useCase = new DeleteFacialEmbeddingUseCase(
    new HttpBiometricDeletionRepository(),
    new LocalEmployeeRepository(),
    new LocalFaceEmbeddingRepository(),
  );

  return useMutation<void, Error, { idEntity: number; idPerson: number }>({
    networkMode: 'always',  // Changed from 'online' to 'always'
    mutationFn: (input) => useCase.execute(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeeKeys.all() });
    },
  });
};

// ════════════════════════════════════════════════════════════════════════════
// OLD IMPLEMENTATION (networkMode: 'online') — COMMENTED FOR ROLLBACK
// ════════════════════════════════════════════════════════════════════════════
// export const useDeleteFacialEmbeddingMutation = () => {
//   const qc = useQueryClient();
//   const useCase = new DeleteFacialEmbeddingUseCase(
//     new HttpBiometricDeletionRepository(),
//     new LocalEmployeeRepository(),
//     new LocalFaceEmbeddingRepository(),
//   );
//
//   return useMutation<void, Error, { idEntity: number; idPerson: number }>({
//     networkMode: 'online',  // OLD: Required connection
//     mutationFn: (input) => useCase.execute(input),
//     onSuccess: () => {
//       void qc.invalidateQueries({ queryKey: employeeKeys.all() });
//     },
//   });
// };
// ════════════════════════════════════════════════════════════════════════════

/**
 * Mutation: delete a specific NFC biometric record for an employee.
 *
 * networkMode:'online' — deletion is blocked when the kiosk is offline.
 * On success: invalidates employeeKeys.all().
 * On error:   no invalidation.
 *
 * Change: nueva-seccion-de-borrado-perfiles-en-administracion-empleados
 */
export const useDeleteNfcBiometricMutation = () => {
  const qc = useQueryClient();
  const useCase = new DeleteNfcBiometricUseCase(
    new HttpBiometricDeletionRepository(),
    new LocalEmployeeRepository(),
  );

  return useMutation<void, Error, { idEntity: number; idPerson: number; idEntityBiometric: number }>({
    networkMode: 'online',
    mutationFn: (input) => useCase.execute(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeeKeys.all() });
    },
  });
};
